<script lang="ts">
	import type { DatasheetPoiData, GetYourGuideTour } from '../types';
	import { page as pageStore } from '$app/stores';
	import Header from '$lib/components/header/Header.svelte';
	import { computeSearchParamsItinerary } from '$lib/components/itinerary/util';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import { poiScheme } from '$lib/components/pois/constants';
	import SideBar from '$lib/components/pois/datasheet/side-bar/SideBar.svelte';
	import { getBookUrl, isDatasheetSideBarPoiData } from '$lib/components/pois/utils';
	import { isAnyPoiPartner } from '$lib/constants/sponsoredPoi';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import {
		getPoiEventGetYourGuidePayload,
		getPoiEventPayload,
		getPoiPartnerTrackingEvent,
	} from '$lib/services/tracking/googleAnalytics/pois';
	import { getBookLeadPayload, metaTrackBookLead } from '$lib/services/tracking/metaPixel/tracking';
	import { getSearchParamsStore, gotoWithParams } from '$lib/services/urls/searchParams';
	import * as m from '$paraglide/messages';
	import { createEventDispatcher } from 'svelte';
	import { get } from 'svelte/store';
	import DatasheetCarousel from './carousel/DatasheetCarousel.svelte';
	import DatasheetContent from './DatasheetContent.svelte';
	import DatasheetToolbar from './toolbar/DatasheetToolbar.svelte';

	const TITLE_HEIGHT = 32;
	const HANDLE_HEIGHT = 20;
	const SCROLL_MARGIN = 68;
	const THRESHOLD = TITLE_HEIGHT + HANDLE_HEIGHT + SCROLL_MARGIN;

	export let poi: DatasheetPoiData;

	const dispatch = createEventDispatcher<{ close: void }>();

	let datasheetHighlightContainer: HTMLDivElement;
	let isCollapsed = true;
	let isCarouselOpen = false;

	const searchParams = getSearchParamsStore();
	const mapLayoutContext = getMapLayoutContext();
	const {
		poiCoordinates,
		closeDatasheet,
		drawerScrollPosition,
		itineraryStore,
		moveDrawer,
	} = mapLayoutContext;

	function updatePoiCoordinatesAndCloseDatasheet() {
		track({
			action: 'datasheet_see_poi_on_map',
			payload: { action: 'click', ...getPoiEventPayload(poi) },
		});
		$poiCoordinates = poi.coordinates;
		closeDatasheet();
	}

	async function handleItineraryButtonClick() {
		track({
			action: 'datasheet_itinerary_button',
			payload: { action: 'click', ...getPoiEventPayload(poi) },
		});

		itineraryStore.addToItinerary({ coordinates: poi.coordinates, label: poi.name });
		const itinerary = await computeSearchParamsItinerary(get(itineraryStore.itinerary));

		gotoWithParams('/routes', { ...$searchParams, itinerary });
	}

	function getTheForkUrl(poi: DatasheetPoiData) {
		if (poi.__typename === 'RestaurantPOI' || poi.__typename === 'RestaurantMichelinPOI') {
			return poi.theForkUrl ?? undefined;
		}
	}

	$: if (datasheetHighlightContainer) {
		isCollapsed
			= $drawerScrollPosition
			- datasheetHighlightContainer?.offsetTop
			- datasheetHighlightContainer?.offsetHeight
			<= THRESHOLD;
	}
	$: CAROUSEL_OPEN_CLASSES = isCarouselOpen ? '' : 'translate-y--100%';
	$: COLLAPSE_STATE_CLASSES = isCollapsed
		? 'rd-2 delay-500'
		: `rd-0 top-2 md:top-0 ${CAROUSEL_OPEN_CLASSES}`;
	$: CAROUSEL_CONTAINER_COLLAPSE_STATE_CLASSES = isCollapsed
		? `rd-2 translate-y-100%`
		: `rd-0 translate-y-0`;
	$: CAROUSEL_CONTAINER_CLASSES = !isCarouselOpen
		? `transition-transform transform-gpu duration-500 md:translate-y-0 ${CAROUSEL_CONTAINER_COLLAPSE_STATE_CLASSES}`
		: '';

	$: datasheetContainerClass = isDatasheetSideBarPoiData(poi) ? 'md:grid' : 'flex flex-col';

	function trackDatasheetBottomsheet(isCollapsed: boolean) {
		if (!isCollapsed) {
			track({
				action: 'datasheet_bottomsheet',
				payload: {
					action: 'scroll',
					...getPoiEventPayload(poi),
				},
			});
		}
	}

	$: trackDatasheetBottomsheet(isCollapsed);

	function handleSideBarBookClick(event: CustomEvent<{ getYourGuideTour: GetYourGuideTour }>) {
		track({
			action: 'call_to_action',
			payload: {
				cta_type: 'datasheet_book',
				...getPoiEventPayload(poi),
				...getPoiEventGetYourGuidePayload(event.detail.getYourGuideTour),
			},
		});
		metaTrackBookLead(getBookLeadPayload(poi));
	}
	let position: number | 'opened' = 'opened';

	// eslint-disable-next-line svelte/no-immutable-reactive-statements
	$: position = 232;

	function getPosition() {
		if (!isCollapsed) {
			return 84; // approx height of handle + title
		} else if (position === 84 && isCollapsed) {
			return 272;
		} else if (
			poi.__typename !== 'ServiceEVPOI'
			&& poi.__typename !== 'ServiceRestStopPOI'
			&& poi.images.length > 0
			&& isCollapsed
		) {
			return 466; // approx height of title + highlight + description
		} else {
			return 'opened';
		}
	}

	function handleSnapHandleClick() {
		position = getPosition();
		moveDrawer(position, { behavior: 'smooth' });
	}
</script>

<div
	class="flex flex-col relative top-0 w-full gap-4 bg-neutral-weakest md:bg-neutral-weak"
>
	<div class="hidden md:block">
		<Header
			hasRoleBanner
			variant="no-logo"
			leftButton={{ text: m.poi_datasheet__back_to_list(), icon: 'west' }}
			class="md:rd-2 md:bg-neutral-weakest md:shadow-smooth-block md:py-2"
			onLeftButtonClick={() => dispatch('close')}
		/>
	</div>
	<div class="grid-cols-[minmax(0,1fr)_300px] rd-2 md:pb-0 flex-1 gap-2 w-full {datasheetContainerClass}">
		<div class="flex-1 rd-2">
			{#if poi.__typename !== 'ServiceEVPOI' && poi.__typename !== 'ServiceRestStopPOI' && poi.images.length > 0}
				<div
					class="w-full h-70 absolute md:relative md:mb-4 md:block md:rd-2 ${COLLAPSE_STATE_CLASSES}"
				>
					<div class={CAROUSEL_CONTAINER_CLASSES}>
						<DatasheetCarousel
							borderRadius={isCollapsed ? 'rd-2' : 'rd-0'}
							images={poi.images}
							prefixAlt={`${poi.name} - ${poi.address.city || ''}`}
							bind:isCarouselOpen
							on:imageClick={({ detail: { carouselOpen } }) => {
								!carouselOpen
									&& track({
										action: 'datasheet_picture',
										payload: {
											action: 'click',
											...getPoiEventPayload(poi),
										},
									});
							}}
							on:imageScroll={() => {
								track({
									action: 'datasheet_picture',
									payload: {
										action: 'scroll',
										...getPoiEventPayload(poi),
									},
								});
							}}
						/>
					</div>
				</div>
				<!-- Add fixed snappoint that does not move for the image container -->
				<div class="snap relative md:hidden"></div>
				<!-- cover element to avoid image flickering through -->
				<div class="w-full h-70 absolute bg-neutral-weakest rd-2 md:hidden"></div>
			{/if}
			<div class="flex flex-col gap-4 bg-neutral-weakest rd-2 relative md:shadow-smooth-block">
				<DatasheetContent
					{poi}
					{isCollapsed}
					bind:datasheetHighlightContainer
					on:itineraryClick={handleItineraryButtonClick}
					on:learnMoreClick={() => {
						track(getPoiPartnerTrackingEvent(poi));
						metaTrackBookLead(getBookLeadPayload(poi));
					}}
					on:seeOnMap={updatePoiCoordinatesAndCloseDatasheet}
					on:handleClick={handleSnapHandleClick}
				/>
				{#if !isAnyPoiPartner(poi)}
					<div class="sticky flex bottom-0 left-0 w-full bg-neutral-weakest rounded-b-2xl">
						<DatasheetToolbar
							showItineraryButton
							scheme={poiScheme[poi.__typename]}
							isFloating={!isCollapsed}
							bookUrl={getBookUrl(poi, $pageStore, $searchParams)}
							theForkUrl={getTheForkUrl(poi)}
							infoUrl={poi.__typename === 'ServiceRestStopPOI' && poi.infoUrl ? poi.infoUrl : undefined}
							on:itineraryClick={handleItineraryButtonClick}
							on:bookClick={() => {
								track({
									action: 'call_to_action',
									payload: { cta_type: 'datasheet_book', ...getPoiEventPayload(poi) },
								});
								metaTrackBookLead(getBookLeadPayload(poi));
							}}
							on:learnMoreClick={() => {
								track(getPoiPartnerTrackingEvent(poi));
								metaTrackBookLead(getBookLeadPayload(poi));
							}}
						/>
					</div>
				{/if}
			</div>
		</div>

		{#if isDatasheetSideBarPoiData(poi)}
			<SideBar
				class="mb-15"
				{poi}
				on:bookClick={handleSideBarBookClick}
				on:selectDates
				on:clearDates
			/>
		{/if}
	</div>
</div>

<style>

	.snap {
		scroll-snap-align: start;
		scroll-snap-stop: always;
		scroll-margin: 272px;
	}
</style>
